import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReliabotixLogo from './ReliabotixLogo';

const { FiMenu, FiX, FiCalendar } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Solutions Showcase', href: '/solutions' },
    { name: 'Demos', href: '/demos' },
    { name: 'Contact', href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBookCall = (e) => {
    e.preventDefault();
    setShowCalendar(true);
    setIsMenuOpen(false);
  };

  const closeCalendar = () => {
    setShowCalendar(false);
  };

  return (
    <>
      {/* Calendar Modal */}
      <AnimatePresence>
        {showCalendar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={closeCalendar}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl mx-4 max-h-[90vh] bg-dark-900/95 backdrop-blur-xl rounded-3xl border border-dark-700/50 overflow-hidden shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-dark-700/30">
                <div className="flex items-center">
                  <ReliabotixLogo size={40} variant="minimal" animate={false} />
                  <div className="ml-3">
                    <h3 className="text-xl font-bold text-white">Schedule Discovery Call</h3>
                    <p className="text-gray-400 text-sm">Free 30-minute consultation</p>
                  </div>
                </div>
                <button
                  onClick={closeCalendar}
                  className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-dark-700/50"
                  title="Close (ESC)"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              {/* Calendar Content */}
              <div className="h-[600px] bg-gradient-to-br from-dark-800 to-dark-900">
                <iframe
                  src="https://cal.com/guychenya?embed=true"
                  className="w-full h-full border-0"
                  allow="camera;microphone;geolocation"
                  title="Schedule a Call with Reliabotix"
                />
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-dark-700/30 bg-dark-800/50">
                <p className="text-center text-gray-400 text-sm">
                  Press <kbd className="px-2 py-1 bg-dark-600/50 text-gray-300 rounded text-xs">ESC</kbd> to close
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fixed Header with Enhanced Glassmorphism */}
      <header className="fixed top-0 left-0 right-0 z-40 px-4 pt-4">
        <motion.nav
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className={`max-w-6xl mx-auto transition-all duration-500 ${
            scrolled || isMenuOpen ? 'glass-strong' : 'glass-nav'
          } rounded-3xl`}
        >
          <div className="flex justify-between items-center px-6 py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                className="group-hover:shadow-lg group-hover:shadow-primary-500/25 transition-all duration-300 rounded-xl"
              >
                <ReliabotixLogo size={48} variant="minimal" animate={true} />
              </motion.div>
              <span className="text-xl font-bold text-white">
                Reliabotix
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                    location.pathname === item.href
                      ? 'glass-button text-primary-300'
                      : 'text-gray-300 hover:text-white hover:bg-white/5 backdrop-blur-sm'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-3">
              {/* CTA Button - Opens Calendar Modal */}
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <button
                  onClick={handleBookCall}
                  className="hidden lg:flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 glass-button"
                >
                  <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
                  Book Call
                </button>
              </motion.div>

              {/* Mobile menu button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl glass text-gray-300 hover:text-white transition-all duration-300"
              >
                <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="lg:hidden border-t border-white/10 px-6 py-4 glass-strong"
              >
                <div className="space-y-2">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className={`block px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                        location.pathname === item.href
                          ? 'glass-button text-primary-300'
                          : 'text-gray-300 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                  <div className="pt-4 border-t border-white/10 mt-4">
                    <button
                      onClick={handleBookCall}
                      className="flex items-center justify-center w-full px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold glass-button"
                    >
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
                      Book Call
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.nav>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-20"></div>
    </>
  );
};

export default Header;